import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

/**
 * The `<dialog>` shell every "mount it to open it" modal in this product is
 * built on: `ImageUploadDialog`, `MentionPickerDialog`, `CreateEntityDialog`,
 * `MeetingDialog`. Portalled to `document.body` so the dialog is never
 * inside somebody else's form, opened with `showModal` so the browser
 * supplies focus trapping and an inert background, and a click that lands on
 * the dialog element itself (rather than the panel inside it) is a click on
 * the backdrop.
 *
 * (`useConfirm`'s dialog is deliberately not built on this: it is one
 * singleton element toggled open and closed with `showModal`/`close` across
 * the app's lifetime, not a fresh instance mounted per use, and it has no
 * form inside it to guard -- unifying the two would blur a real difference
 * rather than remove a duplicated one.)
 *
 * ## The submit guard
 *
 * `onSubmit` stops a submit event from propagating past this dialog, which
 * matters far more than it looks like it should. A portal takes the dialog's
 * *DOM* out of whatever opened it, but React still propagates its own
 * synthetic events along the *React* tree, where the opener can still be an
 * ancestor -- often a `<form>` (a register item's editor, a meeting's own
 * form, a comment composer). Without this guard, clicking "Insert" in an
 * image or mention dialog opened from a `<MarkdownField>` inside one of
 * those forms does not just insert the reference: the submit event keeps
 * bubbling through the React tree, reaches that ancestor form's own
 * `onSubmit`, and submits *it* too -- with whatever it held a render ago,
 * since the state update this dialog just made has not committed yet. The
 * form saves stale content and, if its own submit handler closes on success
 * (most do), the whole editor closes with it. That is the bug this shell
 * exists to make impossible to reintroduce: every dialog built on it gets
 * the guard for free, so nobody mounting a new one has to remember it or
 * discover it the way this one was found -- reference-picking working from
 * an agenda item's body (never wrapped in a `<form>`) and silently closing
 * the whole editor everywhere else that is a `<form>` (a meeting's purpose,
 * a register item, a comment).
 */
export function ModalDialog({
  titleId,
  onClose,
  children,
}: {
  /** id of the element carrying the dialog's own heading, for
   *  `aria-labelledby`. */
  titleId: string;
  /** Escape (the native `cancel` event) and a click on the backdrop both
   *  call this -- never a submit, which this shell already keeps out. */
  onClose: () => void;
  children: ReactNode;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  return createPortal(
    <dialog
      ref={dialogRef}
      className="modal"
      aria-labelledby={titleId}
      onCancel={onClose}
      onClick={(event) => {
        if (event.target === dialogRef.current) onClose();
      }}
      onSubmit={(event) => event.stopPropagation()}
    >
      {children}
    </dialog>,
    document.body,
  );
}

/**
 * The card inside the dialog: a heading, a way out, a body, and optionally a
 * pinned footer.
 *
 * This existed seven times before it existed once -- the schedule's task
 * panel, both of the users page's dialogs, the integration editor, the
 * platform's grant dialog, the schedule's session review and the cost dialog
 * each carried their own `<dialog ref>` / `showModal` / backdrop-click /
 * `card-header` block. They were the same fifteen lines, and they had quietly
 * drifted: none of them was portalled, none carried `ModalDialog`'s submit
 * guard, and each had reinvented the header's controls.
 *
 * ## The header carries a close, not a Cancel
 *
 * Every one of those copies put a "Cancel" button in the header. Where the
 * form underneath had its own Cancel -- which the cost editors and every
 * entity creator do -- that produced two identical buttons in one dialog, two
 * inches apart, and a reader has to work out whether they mean different
 * things. They do not.
 *
 * So the header holds the dismissal affordance every dialog has and every
 * dialog puts in the same corner (the `×`, beside Escape and the backdrop),
 * and *deciding not to go through with it* stays where the decision is made:
 * next to the button that would go through with it. That is the map detail
 * panel's arrangement, which is what this product had already settled on
 * everywhere it was not copying a dialog.
 */
export function ModalCard({
  title,
  titleId,
  onClose,
  panelClassName,
  closeLabel,
  children,
  footer,
}: {
  title: ReactNode;
  /** id for the heading, so the dialog is labelled by it. */
  titleId: string;
  onClose: () => void;
  /** Extra classes on the panel -- width variants, mostly. */
  panelClassName?: string;
  /** Overrides the close button's accessible name, where the product has a
   *  better sentence for it than "Close". */
  closeLabel?: string;
  /** The card body. Padding lives here; without it the fields sit flush
   *  against the panel edges. */
  children: ReactNode;
  /** Pinned below the scrollable body and ruled off from it, so a primary
   *  action is in the same place whether the form is four fields or forty. */
  footer?: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <ModalDialog titleId={titleId} onClose={onClose}>
      <div className={`modal__panel card${panelClassName ? ` ${panelClassName}` : ''}`}>
        <div className="card-header">
          <h2 className="card-title" id={titleId}>
            {title}
          </h2>
          <div className="card-actions">
            <button
              type="button"
              className="button button--ghost modal__close"
              onClick={onClose}
              aria-label={closeLabel ?? t('editor.close')}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        </div>

        <div className="card-body">{children}</div>

        {footer}
      </div>
    </ModalDialog>
  );
}
